import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { RootState } from '../store/store';
import authService from '../services/authService';
import { deleteUser, changeUserStatus } from '../store/authSlice';
import { User, UserRole } from '../types/user';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Password as PasswordIcon
} from '@mui/icons-material';
import { AppDispatch } from '../store/store';
import UserFormDialog from '../components/Admin/UserFormDialog';

const AdminPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await authService.getAllUsers();
        setUsers(response);
      } catch (error) {
        console.error('Erreur lors de la récupération des utilisateurs:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUsers();
  }, []);
  
  // Rediriger si non connecté ou non admin
  if (!isAuthenticated || user?.role !== UserRole.ADMIN) {
    return <Navigate to="/login" />;
  }
  
  const handleAddUser = () => {
    setSelectedUser(null);
    setOpenAddDialog(true);
  };
  
  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setOpenEditDialog(true);
  };
  
  const handleDeleteClick = (user: User) => {
    setSelectedUser(user);
    setOpenDeleteDialog(true);
  };
  
  const handleDeleteConfirm = async () => {
    if (selectedUser) {
      try {
        await dispatch(deleteUser(selectedUser.id));
        setUsers(users.filter(u => u.id !== selectedUser.id));
        setSnackbar({
          open: true,
          message: 'Utilisateur supprimé avec succès',
          severity: 'success'
        });
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Erreur lors de la suppression de l\'utilisateur',
          severity: 'error'
        });
      }
      setOpenDeleteDialog(false);
    }
  };
  
  const handleChangeStatus = async (user: User) => {
    const newStatus = user.status === 'active' ? 'suspended' : 'active';
    try {
      await dispatch(changeUserStatus({ id: user.id, status: newStatus }));
      setUsers(users.map(u => u.id === user.id ? { ...u, status: newStatus } : u));
      setSnackbar({
        open: true,
        message: `Utilisateur ${newStatus === 'active' ? 'activé' : 'suspendu'} avec succès`,
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Erreur lors du changement de statut',
        severity: 'error'
      });
    }
  };
  
  const handleResetPassword = async () => {
    if (selectedUser) {
      try {
        const result = await authService.resetPassword(selectedUser.id);
        if (result.success && result.temporaryPassword) {
          setTempPassword(result.temporaryPassword);
          setOpenResetDialog(true);
        } else {
          setSnackbar({
            open: true,
            message: 'Erreur lors de la réinitialisation du mot de passe',
            severity: 'error'
          });
        }
      } catch (error) {
        console.error('Erreur lors de la réinitialisation du mot de passe:', error);
        setSnackbar({
          open: true,
          message: 'Erreur lors de la réinitialisation du mot de passe',
          severity: 'error'
        });
      }
    }
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  const handleUserFormSubmit = async (userData: Partial<User>, isNew: boolean) => {
    try {
      if (isNew) {
        // Logique pour ajouter un nouvel utilisateur
        await authService.createUser(userData as Omit<User, 'id' | 'createdAt' | 'updatedAt'>);
        setSnackbar({
          open: true,
          message: 'Utilisateur créé avec succès',
          severity: 'success'
        });
        setOpenAddDialog(false);
      } else if (selectedUser) {
        // Logique pour mettre à jour un utilisateur existant
        await authService.updateUser(selectedUser.id, userData);
        setSnackbar({
          open: true,
          message: 'Utilisateur mis à jour avec succès',
          severity: 'success'
        });
        setOpenEditDialog(false);
      }
      
      // Rafraîchir la liste des utilisateurs
      const updatedUsers = await authService.getAllUsers();
      setUsers(updatedUsers);
    } catch (error) {
      console.error('Erreur lors de la gestion de l\'utilisateur:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la gestion de l\'utilisateur',
        severity: 'error'
      });
    }
  };
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Administration des utilisateurs
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={handleAddUser}
        >
          Ajouter un utilisateur
        </Button>
      </Box>
      
      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nom</TableCell>
                <TableCell>Prénom</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Rôle</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Date de création</TableCell>
                <TableCell>Date de mise à jour</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">Chargement...</TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">Aucun utilisateur trouvé</TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.lastName}</TableCell>
                    <TableCell>{user.firstName}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip 
                        label={user.role === UserRole.ADMIN ? 'Admin' : user.role === UserRole.COACH ? 'Coach' : 'Utilisateur'} 
                        color={user.role === UserRole.ADMIN ? 'primary' : user.role === UserRole.COACH ? 'info' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={user.status === 'active' ? 'Actif' : 'Suspendu'} 
                        color={user.status === 'active' ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{new Date(user.createdAt).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</TableCell>
                    <TableCell>{new Date(user.updatedAt).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</TableCell>
                    <TableCell align="right">
                      <IconButton 
                        color="primary" 
                        onClick={() => handleEditUser(user)}
                        size="small"
                        aria-label="Modifier"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        color={user.status === 'active' ? 'error' : 'success'} 
                        onClick={() => handleChangeStatus(user)}
                        size="small"
                        aria-label={user.status === 'active' ? 'Suspendre' : 'Activer'}
                      >
                        {user.status === 'active' ? <BlockIcon /> : <CheckCircleIcon />}
                      </IconButton>
                      <IconButton 
                        color="info" 
                        onClick={() => {
                          setSelectedUser(user);
                          handleResetPassword();
                        }}
                        size="small"
                        aria-label="Réinitialiser le mot de passe"
                      >
                        <PasswordIcon />
                      </IconButton>
                      <IconButton 
                        color="error" 
                        onClick={() => handleDeleteClick(user)}
                        size="small"
                        aria-label="Supprimer"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Dialog de confirmation de suppression */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir supprimer l'utilisateur {selectedUser?.firstName} {selectedUser?.lastName} ? Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Annuler</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog d'ajout d'utilisateur */}
      <UserFormDialog 
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        onSubmit={(userData) => handleUserFormSubmit(userData, true)}
        title="Ajouter un utilisateur"
        user={null}
      />
      
      {/* Dialog de modification d'utilisateur */}
      <UserFormDialog 
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        onSubmit={(userData) => handleUserFormSubmit(userData, false)}
        title="Modifier l'utilisateur"
        user={selectedUser}
      />
      
      {/* Snackbar pour les notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      {/* Dialog de réinitialisation de mot de passe */}
      <Dialog
        open={openResetDialog}
        onClose={() => setOpenResetDialog(false)}
      >
        <DialogTitle>Mot de passe réinitialisé</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Le mot de passe de {selectedUser?.firstName} {selectedUser?.lastName} a été réinitialisé avec succès. Voici le mot de passe temporaire :
          </DialogContentText>
          <Box 
            sx={{ 
              mt: 2, 
              p: 2, 
              bgcolor: 'background.paper', 
              border: '1px solid #ddd',
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '1.2rem',
              textAlign: 'center'
            }}
          >
            {tempPassword}
          </Box>
          <DialogContentText sx={{ mt: 2, color: 'error.main' }}>
            Veuillez communiquer ce mot de passe à l'utilisateur de manière sécurisée. Il devra le changer lors de sa prochaine connexion.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              navigator.clipboard.writeText(tempPassword || '');
              setSnackbar({
                open: true,
                message: 'Mot de passe copié dans le presse-papier',
                severity: 'success'
              });
            }} 
            color="primary"
          >
            Copier
          </Button>
          <Button onClick={() => setOpenResetDialog(false)} color="primary" autoFocus>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminPage;
